/**
 * Document Management System API client.
 *
 * Mirrors the backend /dms endpoints. Upload flow is browser → S3 presigned
 * PUT → complete-upload — the same pattern used by digitalTwinsApi and the
 * legacy asset_documents flow.
 */
import axios from "axios";
import api from "./api";

// ── Types ──────────────────────────────────────────────────────────

export type LinkableEntityType =
  | "asset"
  | "maintenance_task"
  | "device"
  | "sensor"
  | "risk_assessment"
  | "digital_twin"
  | "point_cloud";

export interface DMSFolder {
  id: string;
  virtual_site_id: string;
  parent_folder_id: string | null;
  name: string;
  path: string;
  is_system: boolean;
  description: string | null;
  document_count: number;
  subfolder_count: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface DMSDocumentVersion {
  id: string;
  document_id: string;
  version_number: number;
  file_name: string;
  file_size_bytes: number;
  content_type: string;
  status: "uploading" | "ready" | string;
  is_current: boolean;
  change_note: string | null;
  uploaded_by: string | null;
  uploaded_by_email: string | null;
  uploaded_by_name: string | null;
  uploaded_at: string;
}

export interface DMSDocumentLink {
  id: string;
  document_id: string;
  entity_type: LinkableEntityType;
  entity_id: string;
  created_at: string;
}

export type DMSDocumentType =
  | "pdf" | "image" | "csv" | "xlsx" | "json" | "xml"
  | "docx" | "text" | "other";

export interface DMSDocument {
  id: string;
  virtual_site_id: string;
  folder_id: string | null;
  folder_path: string | null;
  name: string;
  description: string | null;
  document_type: DMSDocumentType;
  tags: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  versions: DMSDocumentVersion[];
  links: DMSDocumentLink[];
}

export interface DMSDocumentList {
  items: DMSDocument[];
  total: number;
}

export interface InitiateUploadPayload {
  virtual_site_id: string;
  document_id?: string;
  folder_id?: string | null;
  folder_path?: string;
  name?: string;
  description?: string | null;
  tags?: string | null;
  file_name: string;
  file_size_bytes: number;
  content_type: string;
  change_note?: string | null;
  link_entity_type?: LinkableEntityType;
  link_entity_id?: string;
}

export interface InitiateUploadResponse {
  document_id: string;
  version_id: string;
  version_number: number;
  upload_url: string | null;
  s3_key: string;
  expires_in: number;
  folder_id: string | null;
}

export interface DMSViewURL {
  version_id: string;
  view_url: string | null;
  expires_in: number;
  content_type: string;
  file_name: string;
  document_type: DMSDocumentType;
}

// ── Folders ────────────────────────────────────────────────────────

export async function listFolders(virtual_site_id: string): Promise<DMSFolder[]> {
  const { data } = await api.get<DMSFolder[]>("/dms/folders", {
    params: { virtual_site_id },
  });
  return data;
}

export async function createFolder(payload: {
  virtual_site_id: string;
  name: string;
  parent_folder_id?: string | null;
  description?: string | null;
}): Promise<DMSFolder> {
  const { data } = await api.post<DMSFolder>("/dms/folders", payload);
  return data;
}

export async function updateFolder(
  folderId: string,
  payload: {
    name?: string;
    description?: string | null;
    parent_folder_id?: string | null;
  },
): Promise<DMSFolder> {
  const { data } = await api.patch<DMSFolder>(`/dms/folders/${folderId}`, payload);
  return data;
}

export async function deleteFolder(folderId: string, recursive = false): Promise<void> {
  await api.delete(`/dms/folders/${folderId}`, { params: { recursive } });
}

// ── Documents ──────────────────────────────────────────────────────

export interface ListDocumentsParams {
  virtual_site_id: string;
  folder_id?: string | null;
  include_subfolders?: boolean;
  search?: string;
  document_type?: DMSDocumentType;
  linked_entity_type?: LinkableEntityType;
  linked_entity_id?: string;
  limit?: number;
  offset?: number;
}

export async function listDocuments(
  params: ListDocumentsParams,
): Promise<DMSDocumentList> {
  // FastAPI doesn't accept folder_id=null as a query string; omit the field
  // when caller wants root-only listing.
  const queryParams: Record<string, unknown> = { ...params };
  if (params.folder_id === null || params.folder_id === undefined) {
    delete queryParams.folder_id;
  }
  const { data } = await api.get<DMSDocumentList>("/dms/documents", {
    params: queryParams,
  });
  return data;
}

export async function getDocument(documentId: string): Promise<DMSDocument> {
  const { data } = await api.get<DMSDocument>(`/dms/documents/${documentId}`);
  return data;
}

export async function updateDocument(
  documentId: string,
  payload: {
    name?: string;
    description?: string | null;
    tags?: string | null;
    folder_id?: string | null;
  },
): Promise<DMSDocument> {
  const { data } = await api.patch<DMSDocument>(`/dms/documents/${documentId}`, payload);
  return data;
}

export async function deleteDocument(documentId: string): Promise<void> {
  await api.delete(`/dms/documents/${documentId}`);
}

// ── Upload ─────────────────────────────────────────────────────────

export async function initiateUpload(
  payload: InitiateUploadPayload,
): Promise<InitiateUploadResponse> {
  const { data } = await api.post<InitiateUploadResponse>(
    "/dms/documents/initiate-upload",
    payload,
  );
  return data;
}

export async function completeUpload(versionId: string): Promise<DMSDocumentVersion> {
  const { data } = await api.post<DMSDocumentVersion>(
    `/dms/documents/versions/${versionId}/complete-upload`,
  );
  return data;
}

export async function deleteVersion(versionId: string): Promise<void> {
  await api.delete(`/dms/documents/versions/${versionId}`);
}

export async function setVersionCurrent(
  versionId: string, isCurrent: boolean,
): Promise<DMSDocumentVersion> {
  const { data } = await api.put<DMSDocumentVersion>(
    `/dms/documents/versions/${versionId}/set-current`,
    null,
    { params: { is_current: isCurrent } },
  );
  return data;
}

export async function getVersionViewURL(versionId: string): Promise<DMSViewURL> {
  const { data } = await api.get<DMSViewURL>(
    `/dms/documents/versions/${versionId}/view-url`,
  );
  return data;
}

/**
 * Run the full browser→S3 upload sequence: initiate → PUT to S3 → complete.
 *
 * onProgress receives 0–1 during the S3 PUT step. In dev mode (upload_url is
 * null) the PUT step is skipped and we go straight to complete-upload.
 */
export async function uploadFile(
  payload: InitiateUploadPayload,
  file: File,
  onProgress?: (frac: number) => void,
): Promise<{ document_id: string; version_id: string }> {
  const init = await initiateUpload(payload);
  if (init.upload_url) {
    // axios.create() configures a JSON Content-Type by default; we need the
    // raw file's content_type to match the signed PUT URL, so use a fresh
    // axios instance.
    await axios.put(init.upload_url, file, {
      headers: { "Content-Type": payload.content_type },
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(e.loaded / e.total);
      },
    });
  }
  await completeUpload(init.version_id);
  return { document_id: init.document_id, version_id: init.version_id };
}

// ── Links ──────────────────────────────────────────────────────────

export async function listDocumentLinks(documentId: string): Promise<DMSDocumentLink[]> {
  const { data } = await api.get<DMSDocumentLink[]>(`/dms/documents/${documentId}/links`);
  return data;
}

export async function linkDocument(
  documentId: string,
  entity_type: LinkableEntityType,
  entity_id: string,
): Promise<DMSDocumentLink> {
  const { data } = await api.post<DMSDocumentLink>(
    `/dms/documents/${documentId}/links`,
    { entity_type, entity_id },
  );
  return data;
}

export async function unlinkDocument(linkId: string): Promise<void> {
  await api.delete(`/dms/documents/links/${linkId}`);
}

// ── Helpers ────────────────────────────────────────────────────────

/** Best-effort MIME type for a File the user picked. */
export function inferContentType(file: File): string {
  if (file.type) return file.type;
  // The browser sometimes returns "" for less-common extensions (e.g. .json).
  const name = file.name.toLowerCase();
  if (name.endsWith(".json")) return "application/json";
  if (name.endsWith(".csv")) return "text/csv";
  if (name.endsWith(".xml")) return "text/xml";
  if (name.endsWith(".txt")) return "text/plain";
  if (name.endsWith(".pdf")) return "application/pdf";
  return "application/octet-stream";
}
